import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, useRoutes } from 'react-router-dom';
import routes from './Site/Routes/routes';
import Loader from './Site/Loader/Loader';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Provider } from 'react-redux';
import ScrollToTop from './Site/utils/contexts/ScrollTop';
import { store } from './redux/store';
import { ErrorBoundary } from 'react-error-boundary';
import BuilderService from './Site/services/BuilderService';
import { Toaster } from 'sonner';

/**
 * Custom hook to hide Froala editor elements
 */
const useFroalaElementsHider = () => {
  useEffect(() => {
    // Apply CSS to hide elements
    const style = document.createElement('style');
    style.textContent = `
      div[style*="z-index:9999"][style*="width:100%"][style*="position:relative"],
      div[style*="z-index: 9999"][style*="width: 100%"][style*="position: relative"],
      p[data-f-id="pbf"],
      .fr-second-toolbar {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Clean up
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
};

const App: React.FC = () => {
  // Use the custom hook to hide Froala elements
  useFroalaElementsHider();

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // This will be handled within the ErrorFallback component
      }}
    >
      <Router>
        <ScrollToTop>
          <Provider store={store}>
            <ToastContainer 
              autoClose={1000} 
              style={{ zIndex: 10000 }}
            />
            <Toaster richColors />
            <Suspense fallback={<Loader isLoading={true} imageUrl="https://dlvkyia8i4zmz.cloudfront.net/JllcrYgcTSeRguZ1fEhe_advance_t_b__2___3_.svg" text="" />}>
              <AppRoutes />
            </Suspense>
          </Provider>
        </ScrollToTop>
      </Router>
    </ErrorBoundary>
  );
};

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

function ErrorFallback({error, resetErrorBoundary}: ErrorFallbackProps) {
  const [isLoading, setIsLoading] = useState(false);
  const builderService = new BuilderService();
  
  // Get current theme from localStorage
  const currentTheme = localStorage.getItem('theme') || 'light';
  const isDark = currentTheme === 'dark';
  
  // Extract the appName from URL if available
  const urlParts = window.location.href.split("/")[4]?.split("&") || [];
  const appName = urlParts.length > 0 ? urlParts[0] : null;
  
  // Regenerate function that uses the actual BuilderService
  const handleRegenerate = async () => {
    setIsLoading(true);
    
    try {
      const response = await builderService.fixError({
        errorMessage: `When rendring a React application crash that shows a red error screen, please:

1. Analyze the following error message and identify the root cause:
   - Error message: ${error.message || error.toString()}

2. Examine my JSX code for common React rendering issues:
   - Invalid element types
   - String expectation or class function or missing arguments. 
   - Non-renderable values (objects, functions passed directly to JSX)
   - Missing required props or invalid prop types
   - Component return statements that don't return valid JSX
   - Missing key props in lists/arrays
   - Infinite re-rendering loops in useEffect or state updates


3. Choose one approach:
   - A minimal fixed version of this tool that resolves the immediate error
   - A more robust implementation with error boundaries
   - Try to change the structure and see if somewhere heavy operations are performing,

Please focus on creating a working solution rather than returning similar kind of error code. I need code that will render properly in my React environment. And I don't want that red screen Please.`,
        appName: appName
      });
      
      console.log("Error sent successfully:", response);
      
      // Call the resetErrorBoundary to clear the error state
      resetErrorBoundary();
      
      // Reload the page to reflect the changes
      window.location.reload();
    } catch (err) {
      console.error("Error sending error to backend:", err);
      setIsLoading(false);
    }
  };
  
  // If loading show the loader
  if (isLoading) {
    return (
      <div style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: isDark ? '#242424' : '#ffffff',
        zIndex: 9999
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '20px' }}>
            <div className="animate-spin" style={{ display: 'inline-block' }}>
              <svg 
                width="40" 
                height="40" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ color: isDark ? '#f76a93' : '#fb5f65' }}
              >
                <circle 
                  className="opacity-25" 
                  cx="12" 
                  cy="12" 
                  r="10" 
                  stroke="currentColor" 
                  strokeWidth="4"
                />
                <path 
                  className="opacity-75" 
                  fill="currentColor" 
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          </div>
          <p style={{ 
            color: isDark ? '#f8f8f8' : '#222222', 
            fontSize: '16px',
            fontWeight: 500
          }}>
            Regenerating...
          </p>
        </div>
      </div>
    );
  }
  
  // Error UI with theme support
  return (
    <div 
      role="alert" 
      style={{
        background: isDark ? '#242424' : '#ffffff',
        color: isDark ? 'white' : '#222222',
        border: `1px solid ${isDark ? '#464646' : '#e1e1e1'}`,
        padding: '1rem',
        margin: '1rem',
        borderRadius: '0.375rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '600px',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: '2rem'
      }}
    >
      <h2 style={{ 
        color: isDark ? '#f8f8f8' : '#111111', 
        fontWeight: 'bold',
        fontSize: '1.125rem',
        lineHeight: '1.75rem',
        marginBottom: '0.75rem'
      }}>
        Something went wrong
      </h2>
      
      <pre style={{ 
        background: isDark ? '#303030' : '#f5f5f5',
        color: isDark ? '#eaeaea' : '#444444',
        padding: '0.75rem',
        marginTop: '0.5rem',
        marginBottom: '1rem',
        borderRadius: '0.375rem',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
      }}>
        Please regenerate. If it still doesn't work, contact the support team. The most likely reason is that your tool has become too heavy, or the AI generated an unhandled element.
      </pre>
      
      <button
        onClick={handleRegenerate}
        disabled={isLoading}
        style={{
          backgroundColor: isDark ? '#fb5f65' : '#fb5f65',
          color: isDark ? '#E0E0E0' : '#ffffff',
          padding: '0.5rem 1rem',
          marginTop: '0.5rem',
          borderRadius: '0.375rem',
          border: 'none',
          cursor: 'pointer',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = isDark ? '#fc787c' : '#fc787c';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        onMouseOut={(e) => {
          if (!isLoading) {
            e.currentTarget.style.backgroundColor = isDark ? '#fb5f65' : '#fb5f65';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <span style={{ marginRight: '8px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 20C9.63273 20 7.57545 19.1043 6.04091 17.5698C4.50636 16.0352 3.61091 14.0457 3.61091 11.7011C3.61091 9.35648 4.50636 7.36705 6.04091 5.83251C7.57545 4.29796 9.63273 3.40251 12 3.40251C14.3673 3.40251 16.4245 4.29796 18.0932 5.83251C19.7618 7.36705 20.6573 9.35648 20.6573 11.7011L18.8618 11.7011C18.8618 9.87329 18.1861 8.32126 16.9675 7.10261C15.7488 5.88397 14.1968 5.20819 12.369 5.20819C10.5412 5.20819 8.98915 5.88397 7.77051 7.10261C6.55186 8.32126 5.87607 9.87329 5.87607 11.7011C5.87607 13.5289 6.55186 15.0809 7.77051 16.2995C8.98915 17.5182 10.5412 18.194 12.369 18.194L12 20Z" fill="currentColor"/>
            <path d="M16 8L20.6569 12.0001L16 16.0002" stroke="currentColor" strokeWidth="2"/>
          </svg>
        </span>
        Regenerate
      </button>
    </div>
  );
}

const AppRoutes: React.FC = () => {
  const routing = useRoutes(routes);
useEffect(() => {
  const hostname = window.location.hostname;
  if (hostname.startsWith("www.")) {
    const newURL = window.location.href.replace("www.", "");
    window.location.replace(newURL);
  }
}, []);


  return <>{routing}</>;
};

export default App;